/** Waist (numeric) ↔ letter size mapping — 26/XXS, 28/XS, 30/S, 32/M, … (+2 per step). */
export const WAIST_TO_ALPHA = {
	"26": "XXS",
	"28": "XS",
	"30": "S",
	"32": "M",
	"34": "L",
	"36": "XL",
	"38": "2XL",
	"40": "3XL",
	"42": "4XL",
	"44": "5XL",
};

export const ALPHA_TO_WAIST = Object.fromEntries(
	Object.entries(WAIST_TO_ALPHA).map(([waist, alpha]) => [alpha, waist])
);
ALPHA_TO_WAIST.XXL = "38";

const LETTER_SIZE_RANK = {
	"2XS": 0,
	XS: 1,
	XXS: 2,
	S: 3,
	M: 4,
	L: 5,
	XL: 6,
	XXL: 7,
	"2XL": 7,
	"3XL": 8,
	"4XL": 9,
	"5XL": 10,
};

export function normalizeRawSize(sz) {
	return String(sz || "")
		.trim()
		.replace(/\s+/g, "")
		.toUpperCase();
}

export function getSizeRank(sz) {
	const s = normalizeRawSize(sz);

	// Combined waist/alpha label e.g. 30/S, 28/XS
	const combined = s.match(/^(\d+)\/(.+)$/);
	if (combined) return 100 + parseInt(combined[1], 10);

	const yM = s.match(/^(\d+)-\d+Y$/i);
	if (yM) return 200 + parseInt(yM[1], 10);
	if (/^\d+$/.test(s)) return 100 + parseInt(s, 10);

	const alpha = s === "XXL" ? "2XL" : s;
	if (alpha in ALPHA_TO_WAIST) return 100 + parseInt(ALPHA_TO_WAIST[alpha], 10);
	if (alpha in LETTER_SIZE_RANK) return LETTER_SIZE_RANK[alpha];
	return 900;
}

/** Sort key for display labels — numeric waist order first, then youth, then other. */
export function getSizeLabelSortKey(label) {
	const rank = getSizeRank(label);
	if (rank < 900) return rank;
	return 900 + normalizeRawSize(label).charCodeAt(0);
}

/** Display label: 30/S, 28/XS, 26/XXS, 9-10Y, or plain alpha when no waist mapping exists. */
export function formatSizeLabel(rawSize) {
	const s = normalizeRawSize(rawSize);
	if (!s) return "?";

	if (/^\d+-\d+Y$/i.test(s)) return s;

	if (/^\d+$/.test(s)) {
		const alpha = WAIST_TO_ALPHA[s];
		return alpha ? `${s}/${alpha}` : s;
	}

	const alpha = s === "XXL" ? "2XL" : s;
	const waist = ALPHA_TO_WAIST[alpha];
	return waist ? `${waist}/${alpha}` : alpha;
}

/** Normalize snapshot SIZE values into display labels (deduped + size-sorted). */
export function normalizeSizeFilterOptions(rawSizes) {
	const labels = new Set();
	for (const v of rawSizes || []) {
		const label = formatSizeLabel(v);
		if (label && label !== "?") labels.add(label);
	}
	return Array.from(labels).sort((a, b) => {
		const rankDiff = getSizeLabelSortKey(a) - getSizeLabelSortKey(b);
		return rankDiff !== 0 ? rankDiff : a.localeCompare(b);
	});
}

/**
 * Expand UI size labels (e.g. "32/M") into raw SIZE values that may appear in the snapshot
 * ("32", "M", "32/M") so filter-dependencies WHERE clauses can match.
 */
export function expandSizeLabelsForApi(selectedLabels) {
	const out = new Set();
	for (const label of selectedLabels || []) {
		const s = String(label || "").trim();
		if (!s) continue;
		out.add(s);
		const combined = s.match(/^(\d+)\/(.+)$/i);
		if (combined) {
			out.add(combined[1]);
			out.add(combined[2].toUpperCase());
			if (combined[2].toUpperCase() === "2XL") out.add("XXL");
			if (combined[2].toUpperCase() === "XXL") out.add("2XL");
		} else {
			const formatted = formatSizeLabel(s);
			if (formatted && formatted !== "?" && formatted !== s) {
				out.add(formatted);
				const again = formatted.match(/^(\d+)\/(.+)$/i);
				if (again) {
					out.add(again[1]);
					out.add(again[2].toUpperCase());
				}
			}
		}
	}
	return Array.from(out);
}

export function getProductSizeLabels(product) {
	const rows = Array.isArray(product?.size_soh_breakdown) ? product.size_soh_breakdown : [];
	const labels = new Set();
	for (const row of rows) {
		const label = formatSizeLabel(row.size || row.size_label);
		if (label && label !== "?") labels.add(label);
	}
	return labels;
}

export function productMatchesSizeFilter(product, selectedLabels) {
	if (!selectedLabels || selectedLabels.length === 0) return true;
	const productLabels = getProductSizeLabels(product);
	return selectedLabels.some((lbl) => productLabels.has(lbl));
}

function gcdPair(a, b) {
	let x = Math.abs(a);
	let y = Math.abs(b);
	while (y) {
		const t = y;
		y = x % y;
		x = t;
	}
	return x || 1;
}

function gcdArray(nums) {
	if (!nums.length) return 1;
	return nums.reduce((acc, n) => gcdPair(acc, n));
}

/** Alpha token for WH ratio display — maps numeric waist to letter; 2XL shown as XXL. */
export function getAlphaFromRawSize(rawSize) {
	const s = normalizeRawSize(rawSize);
	if (!s) return null;
	if (/^\d+-\d+Y$/i.test(s)) return s;
	if (/^\d+$/.test(s)) return WAIST_TO_ALPHA[s] || s;
	if (s === "2XL") return "XXL";
	return s;
}

/** WH SOH quantities keyed by alpha size (numeric waist rolled into letter). */
export function getProductWhSizeQuantities(product) {
	const rows = Array.isArray(product?.size_soh_breakdown) ? product.size_soh_breakdown : [];
	const qtyByAlpha = new Map();
	for (const row of rows) {
		const wh = Number(row.wh_soh) || 0;
		if (wh <= 0) continue;
		const alpha = getAlphaFromRawSize(row.size || row.size_label);
		if (!alpha) continue;
		qtyByAlpha.set(alpha, (qtyByAlpha.get(alpha) || 0) + wh);
	}
	return qtyByAlpha;
}

/** Normalized minimum integer ratio from WH SOH, e.g. [[M,2],[L,2],[XL,1]]. */
export function getProductWhNormalizedRatio(product) {
	const qtyByAlpha = getProductWhSizeQuantities(product);
	const entries = [...qtyByAlpha.entries()].filter(([, q]) => q > 0);
	if (entries.length === 0) return null;
	const g = gcdArray(entries.map(([, q]) => q));
	return entries
		.sort((a, b) => getSizeRank(a[0]) - getSizeRank(b[0]))
		.map(([alpha, q]) => [alpha, q / g]);
}

/** "XS 1, S 1, M 2, L 2, XL 1, XXL 1" */
export function formatWhRatioLabel(ratioEntries) {
	if (!ratioEntries || ratioEntries.length === 0) return "";
	return ratioEntries.map(([alpha, q]) => `${alpha} ${q}`).join(", ");
}

export function getProductWhRatioKey(product) {
	const ratio = getProductWhNormalizedRatio(product);
	return ratio ? formatWhRatioLabel(ratio) : null;
}

/** Parse ratio key into minimum WH requirements per alpha size. */
export function parseWhRatioKey(ratioKey) {
	if (!ratioKey) return [];
	return ratioKey.split(",").map((part) => {
		const m = part.trim().match(/^(.+?)\s+(\d+)$/);
		if (!m) return null;
		return { alpha: m[1].trim(), min: parseInt(m[2], 10) };
	}).filter(Boolean);
}

/** Product WH SOH meets at least each size/qty in the ratio (higher qty allowed). */
export function productMeetsWhRatioMinimum(product, ratioKey) {
	const requirements = parseWhRatioKey(ratioKey);
	if (requirements.length === 0) return false;
	const qtyByAlpha = getProductWhSizeQuantities(product);
	return requirements.every(({ alpha, min }) => (qtyByAlpha.get(alpha) || 0) >= min);
}

export function productMatchesWhRatioFilter(product, selectedRatioKeys) {
	if (!selectedRatioKeys || selectedRatioKeys.length === 0) return true;
	return selectedRatioKeys.some((key) => productMeetsWhRatioMinimum(product, key));
}

export function compareWhRatioKeys(a, b) {
	const aParts = parseWhRatioKey(a);
	const bParts = parseWhRatioKey(b);
	if (!aParts.length && !bParts.length) return 0;
	if (!aParts.length) return 1;
	if (!bParts.length) return -1;
	const rankDiff = getSizeRank(aParts[0].alpha) - getSizeRank(bParts[0].alpha);
	if (rankDiff !== 0) return rankDiff;
	if (aParts.length !== bParts.length) return aParts.length - bParts.length;
	return a.localeCompare(b);
}

/** Comma-separated WH qty signature, e.g. "XS 1, M 2, L 2" → "1,2,2". */
export function getRatioQuantitySignature(ratioKey) {
	return parseWhRatioKey(ratioKey)
		.map(({ min }) => min)
		.join(",");
}

/** Parse search like "1,2,2,1" or "1 2 2 1" into normalized "1,2,2,1"; null if not numeric. */
export function parseNumericRatioSearchTerm(searchTerm) {
	const trimmed = String(searchTerm || "").trim();
	if (!trimmed || !/^[\d,\s]+$/.test(trimmed)) return null;
	const nums = trimmed
		.split(/[\s,]+/)
		.filter(Boolean)
		.map((n) => parseInt(n, 10));
	if (nums.length === 0 || nums.some((n) => Number.isNaN(n))) return null;
	return nums.join(",");
}

/** Match ratio by numeric qty pattern (1,2,2,1) or alpha label text. */
export function ratioMatchesWhRatioSearch(ratioKey, searchTerm) {
	const q = String(searchTerm || "").trim();
	if (!q) return true;

	const numericPattern = parseNumericRatioSearchTerm(q);
	if (numericPattern) {
		return getRatioQuantitySignature(ratioKey).includes(numericPattern);
	}

	return ratioKey.toLowerCase().includes(q.toLowerCase());
}
